import React, { useState, useEffect } from "react";
import {
	Table,
	Button,
	Modal,
	Form,
	Input,
	Row,
	Col,
	Card,
	Space,
	notification,
	Tag,
} from "antd";
import {
	AppstoreAddOutlined,
	EditOutlined,
	DeleteOutlined,
} from "@ant-design/icons";

import DefaultLayout from "../../components/layout/DefaultLayout";
import Header from "../../components/header";
import { useSelector } from "react-redux";

import depServices from "../../services/depServices";
import ServiceComponent from "../../components/service";
import DepartmentComponent from "../../components/department";

const DepServices = () => {
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [editing, setEditing] = useState(null);
	const [form] = Form.useForm();
	const [currentPage, setCurrentPage] = useState(1);
	const [totalDataCount, setTotalDataCount] = useState(0);
	const [itemsPerPage, setItemsPerPage] = useState(15);
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState([]); // Initial state is an array
	const [currentId, setCurrentId] = useState(null);
	const [serviceIds, setServiceIds] = useState(null);
	const { currentUser } = useSelector((state) => state.user);

	// Corrected function to handle the API response structure
	const getDepServices = async (page = 1) => {
		setLoading(true);
		try {
			const response = await depServices.getDepServices(page, itemsPerPage);
			// The array of items is in response.data
			setData(response.data);
			// Pagination details are in the root of the response object
			setTotalDataCount(response.total);
			setItemsPerPage(response.per_page);
			setCurrentPage(page);
		} catch (error) {
			notification.error({
				message: "Error",
				description: "Failed to fetch department services.",
			});
		} finally {
			setLoading(false);
		}
	};

	const handleDelete = (record) => {
		Modal.confirm({
			title: "Confirm Deletion",
			content: `Are you sure you want to delete the service: ${record.name}?`,
			onOk: async () => {
				setLoading(true);
				try {
					const response = await depServices.deleteSubcategory(record.id);
					if (response.success) {
						notification.success({
							message: "Service Deleted",
							description: response.message,
						});
						getDepServices(currentPage);
					}
				} catch (error) {
					notification.error({
						message: "Error",
						description: error.response.data.message,
					});
				} finally {
					setLoading(false);
				}
			},
		});
	};

	useEffect(() => {
		getDepServices(1); // Fetch initial data
	}, []);

	const showModal = () => {
		setIsModalVisible(true);
	};

	const handleCancel = () => {
		setIsModalVisible(false);
		setEditing(null);
		form.resetFields();
	};

	const handleServiceChange = (values) => {
		form.setFieldValue("service_ids", values);
		setServiceIds(values);
	};

	const handleEdit = (record) => {
		setEditing(record);
		setIsModalVisible(true);
		setCurrentId(record.id);
		const serviceIds = record.services.map((service) => service.id);
		form.setFieldsValue({
			service_ids: serviceIds,
		});
	};

	const onFinish = async (values) => {
		setLoading(true);

		try {
			const response = await depServices.postAttachServices(currentId, values);
			if (response.id) {
				setIsModalVisible(false);
				setEditing(null);
				form.resetFields();
				notification.success({
					message: "Services Attached",
					description:
						"Services have been successfully attached to the department.",
				});
				getDepServices(currentPage);
			}
		} catch (error) {
			notification.error({
				message: "Error",
				description: error.response.data.message,
			});
		} finally {
			setLoading(false);
		}
	};

	const columns = [
		{
			title: "Departments",
			dataIndex: "name",
			key: "name",
			sorter: (a, b) => a.name.localeCompare(b.name),
		},
		{
			title: "Services",
			dataIndex: "services",
			key: "services",
			render: (services) => {
				return services.length > 0 ? (
					services.map((service) => (
						<Tag key={service.id} color='green'>
							{service.name}
						</Tag>
					))
				) : (
					<Tag color='red'>No Services</Tag>
				);
			},
		},
		{
			title: "Actions",
			key: "actions",
			fixed: "right",
			width: 80,
			render: (text, record) => (
				<Space>
					<div onClick={() => handleEdit(record)} style={{ cursor: "pointer" }}>
						<EditOutlined />
					</div>
				</Space>
			),
		},
	];

	return (
		<DefaultLayout>
			<Row gutter={[24, 24]}>
				<Col span={24}>
					<Header icon={<AppstoreAddOutlined />} title='Department Services' />
				</Col>
			</Row>

			<Card>
				<Table
					scroll={{ x: 1000 }}
					style={{ minHeight: "100vh" }}
					columns={columns}
					dataSource={data.map((item) => ({ ...item, key: item.id }))}
					pagination={{
						total: totalDataCount,
						current: currentPage,
						pageSize: itemsPerPage,
						showSizeChanger: false,
						onChange: (page, pageSize) => getDepServices(page),
					}}
					loading={loading}
				/>
			</Card>

			<Modal
				title='Attach Service to Department'
				open={isModalVisible}
				onCancel={handleCancel}
				footer={null}
				width={650}
			>
				<Form
					form={form}
					onFinish={onFinish}
					layout='vertical'
					className='mt-20'
				>
					<Form.Item
						name='service_ids'
						label='Service'
						rules={[{ required: true, message: "Please select a Service" }]}
					>
						<ServiceComponent
							multi={true}
							value={serviceIds}
							onChange={handleServiceChange}
						/>
					</Form.Item>

					<Form.Item>
						<Button type='primary' htmlType='submit' loading={loading}>
							{editing ? "Save Changes" : "Add Category"}
						</Button>
					</Form.Item>
				</Form>
			</Modal>
		</DefaultLayout>
	);
};

export default DepServices;
